import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Box,
  ClickAwayListener,
  DatePickerInput,
  Divider,
  FormControl,
  Grid,
  Icon,
  IconButton,
  Icons,
  List,
  ListItem,
  ListItemText,
  Popover,
  SimpleDialog,
  Stack,
  styled,
  TimePickerInput,
  Tooltip,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from '@nextiva/next-ui-components';
import { centerTransformOrigin, newTransformOrigin, centerAnchorOrigin, topLeftAnchorOrigin } from '../../constants/popoverConfig';
import {
  StyledLabel,
} from './AvatarMenu.styled';
import { dndUntilOptions } from '../../utils';
import { isValid, parseISO } from 'date-fns';
import { PRESENCE_STATUS } from '../../constants';
import { useMediaPredicate } from 'react-media-hook';
import { LocalizationProvider, AdapterDateFns } from '@nextiva/next-ui-components';
    
const dndUntilId = 'global-header-dndUntil';
const blockEl = `${dndUntilId}-settings`;
const selectDNDUntilTooltip = 'After the set duration period, your presence will be updated to Automatic.';
const saveDNDUntilLabel = 'Save';
const cancelDNDUntilLabel = 'Cancel';

const StyledGridContainer = styled(Grid)`
  margin: -8px;
`;
const StyledGridItem = styled(Grid)`
  &.MuiGrid-item {
    padding: 8px;
  }
`;
const StyledTimePickerInput = styled(TimePickerInput)`
width: 100%;
.MuiAutocomplete-root {
  width: 100%;
}
&.m-nextiva-global-header-dndUntil__custom-dialog__popup {
  margin-top: unset;
  z-index: unset;
}
.MuiTextField-root {
  vertical-align: unset;
}
& .MuiAutocomplete-listbox {
  max-height: 196px;
}
`;

const StyledRadioTypography = styled(Typography)`
  margin-left: 0;
`;

export const DNDSettingsPopover = ({
  setIsAvatarMenuOpen,
  setIsPresenceMenuOpen,
  isDNDSettingsOpen,
  setIsDNDSettingsOpen,
  dndExpiresAtDateTime,
  updatePresence,
  customDNDUntilDate,
  setCustomDNDUntilDate,
  customDNDUntilTime,
  setCustomDNDUntilTime,
  menuRef
}) => {
  const isMobileDevice = useMediaPredicate('(max-width: 414px)');
  const setDNDUntilTitle = isMobileDevice ? 'Do not disturb until' : 'Set duration';
  const customDndRef = useRef();
  const [selectedDndUntil, setSelectedDndUntil] = useState(dndUntilOptions.dndForever.selectOptionValue);
  const [showTimepickerPopup, setShowTimepickerPopup] = useState(false);
  const [isSetCustomDNDUntilOpen, setIsSetCustomDNDUntilOpen] = useState(false);
  const [isSetCustomDNDUntilSectionOpen, setIsSetCustomDNDUntilSectionOpen] = useState(false);

  useEffect(() => {
    if (isMobileDevice && isSetCustomDNDUntilOpen) {
      try {
        const dndUntilDateTime = isNaN(Array.from(dndExpiresAtDateTime)[0]) ? dndExpiresAtDateTime : parseISO(dndExpiresAtDateTime);
        const dtValue = isValid(dndUntilDateTime) ? dndUntilDateTime : new Date();
        setCustomDNDUntilDate(dtValue);
        setCustomDNDUntilTime(dtValue);
      } catch (e) {
        console.error(
          'There was an error while setting values to the date fields',
          e
        );
      }
    }
  }, [isMobileDevice, isSetCustomDNDUntilOpen, setCustomDNDUntilDate, setCustomDNDUntilTime]);
  
  const handleSetDNDUntil = (dndFor) => {
    if (!dndFor) {
      updatePresence({ status: PRESENCE_STATUS.DO_NOT_DISTURB }, '');
    } else {
     const dndUntilDateTime = dndFor();
      updatePresence({ status: PRESENCE_STATUS.DO_NOT_DISTURB, expiresAtDateTime: dndUntilDateTime }, '');
    }
    setIsDNDSettingsOpen(false);
    setIsPresenceMenuOpen(false);
    setIsAvatarMenuOpen(false);
  };

  const handleOpenSetCustomDNDUntil = () => {
    setIsSetCustomDNDUntilOpen(true);
  };

  const handleCustomDNDUntilDateChange = (value) =>  {
    setCustomDNDUntilDate(value);
  };
  
  const handleCustomDNDUntilTimeChange = (value) => {
    setCustomDNDUntilTime(value);
  };
  
  const handleTimepickerClickAway = () => {
    setShowTimepickerPopup(false);
  };
  
  const handleSubmitCustomDNDUntil = () => {
    const dndUntilDateTime = new Date(customDNDUntilDate);
    if (typeof customDNDUntilTime === 'string') {
      const todayDate = new Date();
      dndUntilDateTime.setHours(todayDate.getHours(), todayDate.getMinutes(), 0, 0);
    } else {
      dndUntilDateTime.setHours(customDNDUntilTime.getHours(), customDNDUntilTime.getMinutes(), 0, 0);
    }
    updatePresence({ status: PRESENCE_STATUS.DO_NOT_DISTURB, expiresAtDateTime: dndUntilDateTime });
    setIsSetCustomDNDUntilOpen(false);
    setIsDNDSettingsOpen(false);
    setIsPresenceMenuOpen(false);
    setIsAvatarMenuOpen(false);
  };
  
  const handleCancelSetCustomDNDUntil = () => {
    setIsSetCustomDNDUntilOpen(false);
    setIsDNDSettingsOpen(false);
    setIsPresenceMenuOpen(false);
    setIsAvatarMenuOpen(false);
  };
  
  const handleDNDSelectChange = (event) => {
    event.preventDefault();
    setSelectedDndUntil(event.target.value);
    if (event.target.value !== dndUntilOptions.dndForCustom.selectOptionValue){
      setIsSetCustomDNDUntilSectionOpen(false);
    }else {
      setIsSetCustomDNDUntilSectionOpen(true);
      try {
        const dndUntilDateTime = isNaN(Array.from(dndExpiresAtDateTime)[0]) ? dndExpiresAtDateTime : parseISO(dndExpiresAtDateTime);
        const dtValue = (dndExpiresAtDateTime && event.target.value !== dndUntilOptions.dndForever.selectOptionValue && isValid(dndUntilDateTime)) ? dndUntilDateTime : new Date();
        setCustomDNDUntilDate(dtValue);
        setCustomDNDUntilTime(dtValue);
      } catch (e) {
        console.error(
          'There was an error while setting values to the date fields',
          e
        );
      }
    }
  };

  const handleSubmitSelectDNDUntil = () => {
    let dndUntilDateTime;
    switch (selectedDndUntil) {
      case dndUntilOptions.dndForever.selectOptionValue:
        updatePresence({ status: PRESENCE_STATUS.DO_NOT_DISTURB }, '');
        setIsDNDSettingsOpen(false);
        setIsPresenceMenuOpen(false);
        setIsAvatarMenuOpen(false);
        setSelectedDndUntil(dndUntilOptions.dndForever.selectOptionValue);
        break;
      case dndUntilOptions.dndForCustom.selectOptionValue:
        dndUntilDateTime = new Date(customDNDUntilDate);
        if (typeof customDNDUntilTime === 'string') {
          const todayDate = new Date();
          dndUntilDateTime.setHours(todayDate.getHours(), todayDate.getMinutes(), 0, 0);
        } else {
          dndUntilDateTime.setHours(customDNDUntilTime.getHours(), customDNDUntilTime.getMinutes(), 0, 0);
        }
        updatePresence({ status: PRESENCE_STATUS.DO_NOT_DISTURB, expiresAtDateTime: dndUntilDateTime }, '');
        setIsSetCustomDNDUntilSectionOpen(false);
        setIsDNDSettingsOpen(false);
        setIsPresenceMenuOpen(false);
        setIsAvatarMenuOpen(false);
        setSelectedDndUntil(dndUntilOptions.dndForever.selectOptionValue);
        break;
      default:
        dndUntilDateTime = dndUntilOptions[selectedDndUntil].dateTimeValue();
        updatePresence({ status: PRESENCE_STATUS.DO_NOT_DISTURB, expiresAtDateTime: dndUntilDateTime }, '');
        setIsDNDSettingsOpen(false);
        setIsPresenceMenuOpen(false);
        setIsAvatarMenuOpen(false);
        setSelectedDndUntil(dndUntilOptions.dndForever.selectOptionValue);}
  };

  const handleCancelSelectDNDUntil = () => {
    setIsSetCustomDNDUntilSectionOpen(false);
    setIsDNDSettingsOpen(false);
    setIsPresenceMenuOpen(false);
    setIsAvatarMenuOpen(false);
    setSelectedDndUntil(dndUntilOptions.dndForever.selectOptionValue);
  };
  if(isMobileDevice) return (
      <>
        <Popover
            open={isDNDSettingsOpen}
            anchorEl={menuRef}
            onClose={() => setIsDNDSettingsOpen(false)}
            anchorOrigin={centerAnchorOrigin}
            transformOrigin={centerTransformOrigin}
            PaperProps={{
              ref: customDndRef,
              id: `${dndUntilId}-settings`,
            }}
        >
          <Stack sx={{ padding: '32px', width: '560px' }}>
            <Stack sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', paddingBottom: '18px',}}>
              <Box sx={{ color: (theme) => theme.palette.blueGrey['A300'], marginBottom: '8px', marginRight: '8px', lineHeight: '12px', fontSize: '10px'}}>
                <Icon
                    icon={Icons.chevronLeft}
                    color='secondary'
                    data-testid="GlobalHeader-AvatarMenu-SetDNDTitle-Icon"
                />
              </Box>
              <Stack sx={{
                display: 'flex',
                flexDirection: 'row',
                flex: 2,
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}>
                <Box>
                  <StyledLabel>
                    <Typography variant="h5" data-testid="GlobalHeader-AvatarMenu-SetDNDTitle" color='secondaryDarkBlue'>
                      {setDNDUntilTitle}
                    </Typography>
                  </StyledLabel>
                </Box>
                <Box className={`${dndUntilId}__header__close`}>
                  <IconButton
                      sx={{ marginTop: '-4px' }}
                      aria-label="Close"
                      onClick={handleCancelSelectDNDUntil}
                      variant="contained"
                      className={`${dndUntilId}__header__close-wrapper`}
                      data-testid="GlobalHeaderUI-DNDSetting-CloseButton"
                  >
                    <Icon
                        icon={Icons.close}
                        size={16}
                        className={`${dndUntilId}__header__close-wrapper__icon`}
                    />
                  </IconButton>
                </Box>
              </Stack>
            </Stack>
            <List sx={{ paddingBottom: '0px' }}>
              <ListItem
                  button
                  className={`${blockEl}__button`}
                  onClick={() =>
                      handleSetDNDUntil()
                  }
              >
                <ListItemText
                    primary={dndUntilOptions.dndForever.text}
                    className="body2"
                    data-testid="GlobalHeader-AvatarMenu-SetDND-Forever"
                />
              </ListItem>
              <ListItem
                  button
                  className={`${blockEl}__button`}
                  onClick={() =>
                      handleSetDNDUntil(dndUntilOptions.dndFor30Minutes.dateTimeValue)
                  }
              >
                <ListItemText
                    primary={dndUntilOptions.dndFor30Minutes.text}
                    className="body2"
                    data-testid="GlobalHeader-AvatarMenu-SetDND-30minutes"
                />
              </ListItem>
              <ListItem
                  button
                  className={`${blockEl}__button`}
                  onClick={() =>
                      handleSetDNDUntil(dndUntilOptions.dndFor1Hour.dateTimeValue)
                  }
              >
                <ListItemText
                    primary={dndUntilOptions.dndFor1Hour.text}
                    className="body2"
                    data-testid="GlobalHeader-AvatarMenu-SetDND-1hour"
                />
              </ListItem>
              <ListItem
                  button
                  className={`${blockEl}__button`}
                  onClick={() =>
                      handleSetDNDUntil(dndUntilOptions.dndFor6Hours.dateTimeValue)
                  }
              >
                <ListItemText
                    primary={dndUntilOptions.dndFor6Hours.text}
                    className="body2"
                    data-testid="GlobalHeader-AvatarMenu-SetDND-6hour"
                />
              </ListItem>
              <ListItem
                  button
                  className={`${blockEl}__button`}
                  onClick={() =>
                      handleSetDNDUntil(dndUntilOptions.dndForRestOfDay.dateTimeValue)
                  }
              >
                <ListItemText
                    primary={dndUntilOptions.dndForRestOfDay.text}
                    className="body2"
                    data-testid="GlobalHeader-AvatarMenu-SetDND-Today"
                />
              </ListItem>
              <ListItem
                  button
                  className={`${blockEl}__button`}
                  onClick={handleOpenSetCustomDNDUntil}
              >
                <ListItemText
                    primary={dndUntilOptions.dndForCustom.text}
                    className="body2"
                    data-testid="GlobalHeader-AvatarMenu-SetDND-Custom"
                />
              </ListItem>
            </List>
          </Stack>
        </Popover>
        <Popover
            open={isSetCustomDNDUntilOpen}
            anchorEl={customDndRef.current}
            onClose={() => setIsSetCustomDNDUntilOpen(false)}
            anchorOrigin={topLeftAnchorOrigin}
            transformOrigin={newTransformOrigin}
            PaperProps={{
              id: `${dndUntilId}-custom-settings`,
            }}
        >
          <Stack  sx={{ padding: '16px' }}
                  id="dnd-custom-settings"
          >
            <Stack sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              marginTop: '16px',
              marginBottom: '16px',
            }}>
              <StyledLabel htmlFor="dnd-custom-settings">
                <Typography variant="h5" data-testid="GlobalHeader-AvatarMenu-Set-Custom-DNDTitle">
                  {setDNDUntilTitle}
                </Typography>
              </StyledLabel>
              <Box className={`${dndUntilId}__header__close`}>
                <IconButton
                    sx={{ marginTop: '-4px' }}
                    aria-label="Close"
                    onClick={handleCancelSetCustomDNDUntil}
                    variant="contained"
                    className={`${dndUntilId}__header__close-wrapper`}
                    data-testid="NotificationsUI-NotificationsPanel-CloseButton"
                >
                  <Icon
                      icon={Icons.close}
                      className={`${dndUntilId}__header__close-wrapper__icon`}
                  />
                </IconButton>
              </Box>
            </Stack>
            <StyledGridContainer container spacing={2}>
              <StyledGridItem item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePickerInput
                      inputFormat="MM/dd/yyyy"
                      inputProps={{
                        required: true,
                        label: 'Date',
                        fullWidth: true,
                        allowNull: false,
                        id: 'custom-dnd-date',
                        'data-testid':
                            `${dndUntilId}-Custom-DatePanel`,
                      }}
                      onChange={handleCustomDNDUntilDateChange}
                      value={customDNDUntilDate}
                  />
                </LocalizationProvider>
              </StyledGridItem>
              <StyledGridItem item xs={12}>
                <ClickAwayListener onClickAway={handleTimepickerClickAway}>
                  <StyledTimePickerInput
                      autoCompleteProps={{
                        open: showTimepickerPopup,
                      }}
                      textFieldProps={{
                        onBlur: () => setShowTimepickerPopup(false),
                        onFocus: () => setShowTimepickerPopup(true),
                        label: 'Time',
                        id: 'custom-dnd-time',
                        'data-testid':
                            `${dndUntilId}-Custom-TimePanel`,
                        required: true,
                        fullWidth: true,
                        iconProps: {
                          style: { cursor: 'pointer' },
                          onClick: () =>
                              setShowTimepickerPopup(!showTimepickerPopup),
                        },
                      }}
                      onChange={handleCustomDNDUntilTimeChange}
                      step={15}
                      value={customDNDUntilTime}
                      className={`${blockEl}__popup`}
                  />
                </ClickAwayListener>
              </StyledGridItem>
            </StyledGridContainer>
            <Stack
                sx={{
                  display: 'flex',
                  flexDirection:'row',
                  gap: '8px',
                  marginTop: '8px',
                  marginBottom: '16px',
                  marginLeft: 'auto',
                }}
            >
              <Button variant="contained" color="secondary" onClick={handleCancelSetCustomDNDUntil}>
                {cancelDNDUntilLabel}
              </Button>
              <Button variant="contained" color="primary" type="submit" onClick={handleSubmitCustomDNDUntil}>
                {saveDNDUntilLabel}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </>
  );

  return (
      <SimpleDialog confirmText={saveDNDUntilLabel}
                    confirmButtonProps={{
                      variant: 'contained',
                      color: 'primary',
                      type: 'submit',
                      onClick: handleSubmitSelectDNDUntil,
                    }}
                    cancelText={cancelDNDUntilLabel}
                    cancelButtonProps={{
                      disabled: true,
                      variant: 'contained',
                      color: 'secondary',
                      onClick: handleCancelSelectDNDUntil,
                    }}
                    dialogActionProps={{sx: { marginTop: '30px' }}}
                    open={isDNDSettingsOpen}
                    dialogProps={{
                      onClose: () => setIsDNDSettingsOpen(false),
                      PaperProps: {
                        ['aria-modal']: true,
                        sx: {width: '436px'},
                      },
                    }}
                    title={
                      <Stack direction="row"
                             justifyContent="space-between"
                             alignItems="center"
                             spacing={2}
                      >
                        <label>
                          <Typography variant="h5" data-testid="GlobalHeader-AvatarMenu-SetDNDTitle" color='secondaryDarkBlue'>
                            {setDNDUntilTitle}
                          </Typography>
                        </label>
                        <IconButton
                            sx={isMobileDevice ? { marginTop: '-4px' } : null}
                            aria-label="Close"
                            onClick={handleCancelSelectDNDUntil}
                            variant="contained"
                            className={`${dndUntilId}__header__close-wrapper`}
                            data-testid="GlobalHeaderUI-DNDSetting-CloseButton"
                        >
                          <Icon
                              icon={Icons.close}
                              size={isMobileDevice ? 16 : 24}
                              className={`${dndUntilId}__header__close-wrapper__icon`}
                          />
                        </IconButton>
                      </Stack>
                    }
                    dialogTitleProps={{sx: { marginBottom: 4}}}
      >

        <Divider />
        <FormControl required={true} sx={{width: '100%'}}>
          <Stack sx={{ display: 'flex', flexDirection: 'row', paddingTop: '32px', paddingBottom: '8px' }}>
            <FormLabel
                sx={{
                  '&.Mui-focused': {color: (theme) => theme.palette.blueGrey['A600']},
                }}
                required={false}
                htmlFor="dndUntil-select-label"
                id="dndUntil-select-input-label">
              <Typography variant="overline">
                Set my presence to
              </Typography>
            </FormLabel>
          </Stack>
          <Box sx={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: (theme) => theme.palette.blueGrey['A200'],
            borderRadius: '4px',
            padding: '8px',
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon icon={Icons.dnd} size={10} sx={{marginLeft: '8px'}}/>
              <Typography variant="body1">
                Do not disturb
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" sx={{ paddingTop: '24px', paddingBottom: '10px' }}>
            <FormLabel
                sx={{
                  '&.Mui-focused': { color: (theme) => theme.palette.blueGrey['A600'] },
                  display: 'flex',
                  alignItems: 'center'
                }}
                required={false}
                id="status-duration-radio-label">
              <Typography variant="overline">
                For a duration of
              </Typography>
            </FormLabel>
            <Tooltip placement="top" title={selectDNDUntilTooltip}>
              <IconButton
                  aria-label="set dndUntil"
                  id={`${dndUntilId}-select-tooltip`}
              >
                <Icon icon={Icons.help} sx={{width: '12px', height: '12px'}}/>
              </IconButton>
            </Tooltip>
          </Stack>
          <RadioGroup
              value={selectedDndUntil}
              onChange={handleDNDSelectChange}
          >
            <Typography variant="body2">
              <Stack spacing={1.5}>
                <FormControlLabel
                    value={dndUntilOptions.dndForever.selectOptionValue}
                    control={<Radio />}
                    label={<StyledRadioTypography variant="body2">{dndUntilOptions.dndForever.text}</StyledRadioTypography>} />
                <FormControlLabel
                    value={dndUntilOptions.dndFor30Minutes.selectOptionValue}
                    control={<Radio />}
                    label={<StyledRadioTypography variant="body2">{dndUntilOptions.dndFor30Minutes.text}</StyledRadioTypography>} />
                <FormControlLabel
                    value={dndUntilOptions.dndFor1Hour.selectOptionValue}
                    control={<Radio />}
                    label={<StyledRadioTypography variant="body2">{dndUntilOptions.dndFor1Hour.text}</StyledRadioTypography>} />
                <FormControlLabel
                    value={dndUntilOptions.dndFor6Hours.selectOptionValue}
                    control={<Radio />}
                    label={<StyledRadioTypography variant="body2">{dndUntilOptions.dndFor6Hours.text}</StyledRadioTypography>} />
                <FormControlLabel
                    value={dndUntilOptions.dndForRestOfDay.selectOptionValue}
                    control={<Radio />}
                    label={<StyledRadioTypography variant="body2">{dndUntilOptions.dndForRestOfDay.text}</StyledRadioTypography>} />
                <FormControlLabel
                    value={dndUntilOptions.dndForCustom.selectOptionValue}
                    control={<Radio />}
                    label={<StyledRadioTypography variant="body2">{dndUntilOptions.dndForCustom.text}</StyledRadioTypography>} />
              </Stack>
            </Typography>
          </RadioGroup>
          {isSetCustomDNDUntilSectionOpen &&
              <Stack  sx={{ padding: '16px 0px' }}
                      id="dnd-custom-settings-section"
              >
                <StyledGridContainer container spacing={2}>
                  <StyledGridItem item xs={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePickerInput
                          inputFormat="MM/dd/yyyy"
                          inputProps={{
                            required: true,
                            label: <Typography component="span" variant="body2" sx={{textTransform: 'capitalize'}}>
                              Date
                            </Typography>,
                            fullWidth: true,
                            allowNull: false,
                            id: 'custom-dnd-date-section',
                            'data-testid':
                                `${dndUntilId}-Custom-DatePanel-section`,
                          }}
                          onChange={handleCustomDNDUntilDateChange}
                          value={customDNDUntilDate}
                      />
                    </LocalizationProvider>
                  </StyledGridItem>
                  <StyledGridItem item xs={6}>
                    <StyledTimePickerInput
                        autoCompleteProps={{
                          open: showTimepickerPopup,
                        }}
                        textFieldProps={{
                          onBlur: () => setShowTimepickerPopup(false),
                          onFocus: () => setShowTimepickerPopup(true),
                          label: <Typography component="span" variant="body2" sx={{textTransform: 'capitalize'}}>
                            Time
                          </Typography>,
                          id: 'custom-dnd-time-section',
                          'data-testid':
                              `${dndUntilId}-Custom-TimePanel-section`,
                          required: true,
                          fullWidth: true,
                          iconProps: {
                            style: { cursor: 'pointer' },
                            onClick: () =>
                                setShowTimepickerPopup(!showTimepickerPopup),
                          },
                        }}
                        onChange={handleCustomDNDUntilTimeChange}
                        step={15}
                        value={customDNDUntilTime}
                        className={`${blockEl}__popup`}
                    />
                  </StyledGridItem>
                </StyledGridContainer>
              </Stack>
          }
        </FormControl>
        <Divider sx={{ paddingTop: 4 }} />
      </SimpleDialog>
  );
};

DNDSettingsPopover.displayName = 'DNDSettingsPopover';
